/**
 * Unified Pages editor — Shopify-theme-editor style (Forge builder v3,
 * ported from growwithhype; replaces the manifest-driven VV editor).
 *
 * Full-screen takeover: renders OUTSIDE AdminShell (see App.tsx) as a
 * fixed inset-0 layout — the document never scrolls, each pane scrolls
 * internally. Topbar carries the nav drawer, page picker, save status and
 * Publish. Left: section list in true page order. Center: live preview.
 * Right: properties for whatever is selected — forms are GENERATED from
 * the section's Zod schema (~/lib/formgen). Autosave everywhere.
 *
 * Roles: layout ops (add/duplicate/delete/reorder/hide sections, new page,
 * delete page) are DEV-ONLY. Content field edits + the page-meta panel
 * stay available to every role.
 *
 * LISTS: the picker also opens the record lists (Drip menu, Services,
 * FAQs, Program steps, Testimonials) — same takeover, rendered by
 * ListWorkspace with the same three panes. Deep link: `/pages?list=drips`
 * (+ `&item=<id>`), or `#list:drips`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LIST_KEYS,
  LIST_META,
  PAGE_TEMPLATES,
  SECTION_META,
  SECTION_TYPES,
  listForSection,
  type ListKey,
  type SectionType,
} from '@vv/sections';
import PagePreview, {
  type PreviewPin,
  type PreviewSectionInfo,
  type PreviewWidth,
  type SectionFieldsItem,
} from '~/components/PagePreview';
import ListWorkspace, { defaultListHref } from '~/components/ListWorkspace';
import ReviewPanel from '~/components/ReviewPanel';
import { ImagePicker } from '~/components/ImagePicker';
import { navGroupsFor, PinnedNav } from '~/components/navConfig';
import { SectionForm } from '~/lib/formgen';
import { authHeaders, signOut } from '~/lib/auth';
import { API_BASE, SITE_ORIGIN } from '~/lib/config';
import { LIST_DEFS } from '~/lib/lists';
import { recordPatches, sectionPatches } from '~/lib/previewPatches';
import { VIEWPORT_LABEL, pinLabel, type ReviewDraft, type ReviewThread } from '~/lib/review';
import { useAuth } from '~/lib/useAuth';
import { useLists } from '~/lib/useLists';
import { useReview } from '~/lib/useReview';
import { usePublish } from '~/lib/usePublish';
import {
  usePageDocs,
  sanitizeSlug,
  buildTemplateSections,
  type PageDoc,
  type SectionDraft,
} from '~/lib/usePageDocs';
import { useGlobalBlocks, type GlobalBlockInfo } from '~/lib/useGlobalBlocks';

type Sel = { kind: 'section'; key: string } | { kind: 'meta' } | null;

/** A spot in the editor — a page (+ selected section) or a list (+ item).
 * Preview clicks that jump between them push the spot they left, so the
 * topbar's "← Back" can return. */
type Place =
  | { kind: 'page'; slug: string; sectionKey: string | null }
  | { kind: 'list'; list: ListKey; item: string | null };

/** Public-site path for a page slug. */
function hrefForSlug(slug: string): string {
  return slug === 'home' ? '/' : `/${slug}`;
}

function isListKey(v: string | null | undefined): v is ListKey {
  return !!v && (LIST_KEYS as string[]).includes(v);
}

/** Where the editor opens: `?list=drips&item=<id>` / `#list:drips` for a
 * list, `?slug=<slug>` / `#<slug>` for a page. */
function initialTarget(): { list: ListKey | null; item: string | null; slug: string } {
  if (typeof window === 'undefined') return { list: null, item: null, slug: 'home' };
  const params = new URLSearchParams(window.location.search);
  const hash = decodeURIComponent(window.location.hash.replace('#', ''));
  const list = params.get('list') ?? (hash.startsWith('list:') ? hash.slice(5) : null);
  if (isListKey(list)) return { list, item: params.get('item'), slug: 'home' };
  return { list: null, item: null, slug: params.get('slug') || (hash.startsWith('list:') ? '' : hash) || 'home' };
}

/** True while focus is in a text-entry control — Esc must not eat input. */
function isTypingTarget(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable
  );
}

/** Short human label for a section row: type label + a content snippet.
 * Global refs label as the shared block's name (the payload lives in the
 * Blocks library, not on this page). */
function sectionRowLabel(
  s: SectionDraft,
  globals?: Map<string, GlobalBlockInfo>,
): { type: string; snippet: string } {
  if (s.data.type === 'global') {
    const block = globals?.get(String(s.data.blockId ?? ''));
    return { type: 'Shared block', snippet: block ? block.name : '⚠ missing block' };
  }
  const meta = SECTION_META[s.data.type as SectionType];
  const typeLabel = meta?.label ?? String(s.data.type);
  for (const k of ['title', 'quote', 'html', 'eyebrow', 'leftText', 'formTitle']) {
    const v = s.data[k];
    if (typeof v === 'string' && v.trim()) {
      const text = v.replace(/<[^>]+>/g, '');
      return { type: typeLabel, snippet: text.length > 34 ? `${text.slice(0, 34)}…` : text };
    }
  }
  return { type: typeLabel, snippet: '' };
}

const EyeOffIcon = () => (
  <svg
    width={13}
    height={13}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// ── Module-level subcomponents ──────────────────────────────────────
// These hold their own state, so they MUST live outside PagesEditor —
// a function component declared inside the parent gets a new identity
// every parent render and React remounts it, wiping its state.

/** Overlay nav drawer — same groups as AdminShell's sidebar (navConfig),
 * so editors see one consistent nav. Links into this editor (`/pages…`)
 * switch in place via onPick instead of routing, and highlight from the
 * editor's own state (`currentTo`) — the editor keeps its URL with
 * replaceState, which the router never sees. */
function NavDrawer({
  email,
  role,
  currentTo,
  onPick,
  onClose,
}: {
  email: string | undefined;
  role: string | undefined;
  currentTo: string;
  onPick: (to: string) => void;
  onClose: () => void;
}) {
  const groups = navGroupsFor(role);
  return (
    <>
      <div className="pages-editor__drawer-backdrop" onClick={onClose} />
      <div className="pages-editor__drawer" role="dialog" aria-modal="true" aria-label="Admin navigation">
        <div className="admin-sidebar__brand">
          Valley Vitality
          <div className="admin-sidebar__role">Operator</div>
        </div>
        <nav className="admin-sidebar__nav">
          {groups.map((group) => (
            <div key={group.label} className="admin-sidebar__section">
              <div className="admin-sidebar__group is-open" style={{ cursor: 'default' }}>
                <span className="admin-sidebar__group-leading">
                  <span className="admin-sidebar__group-icon">{group.icon}</span>
                  <span>{group.label}</span>
                </span>
              </div>
              <div className="admin-sidebar__group-items">
                {group.items.map((item) =>
                  item.to.startsWith('/pages') ? (
                    <a
                      key={item.to}
                      href={item.to}
                      onClick={(e) => {
                        e.preventDefault();
                        onPick(item.to);
                        onClose();
                      }}
                      className={`admin-sidebar__link${item.to === currentTo ? ' is-active' : ''}`}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `admin-sidebar__link${isActive ? ' is-active' : ''}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ),
                )}
              </div>
            </div>
          ))}
        </nav>
        <div style={{ paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <PinnedNav onNavigate={onClose} />
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ padding: '8px 12px', fontSize: 13, color: 'rgba(255,255,255,0.78)' }}>
            {email}
          </div>
          <button
            type="button"
            onClick={() => signOut()}
            style={{
              width: '100%',
              padding: '8px 12px',
              textAlign: 'left',
              fontSize: 13,
              color: 'rgba(255,255,255,0.55)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 8,
            }}
          >
            Sign out →
          </button>
        </div>
      </div>
    </>
  );
}

/** Searchable add-section palette — modal over the editor. Shared blocks
 * (from the Blocks library) get their own group up top. */
function SectionPalette({
  onAdd,
  onAddGlobal,
  globals,
  onClose,
}: {
  onAdd: (t: SectionType) => void;
  onAddGlobal: (blockId: string) => void;
  globals: GlobalBlockInfo[];
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const matches = SECTION_TYPES.filter((t) => {
    if (!query) return true;
    const m = SECTION_META[t];
    return `${m.label} ${m.hint}`.toLowerCase().includes(query);
  });
  const globalMatches = globals.filter((g) => {
    if (!query) return true;
    return `${g.name} ${String(g.section.type ?? '')}`.toLowerCase().includes(query);
  });
  return (
    <div className="pages-editor__palette-backdrop" onClick={onClose}>
      <div
        className="pages-editor__palette"
        role="dialog"
        aria-modal="true"
        aria-label="Add section"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            Add section
          </span>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}
            aria-label="Close section palette"
          >
            ×
          </button>
        </div>
        <input
          autoFocus
          placeholder="Search sections…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            width: '100%',
            fontSize: 13.5,
            padding: '8px 10px',
            border: '1px solid var(--line)',
            borderRadius: 8,
            boxSizing: 'border-box',
          }}
        />
        <div className="pages-editor__palette-list">
          {globalMatches.length > 0 && (
            <>
              <p style={{ margin: '2px 2px 0', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--teal)' }}>
                Shared blocks — one edit updates every page
              </p>
              {globalMatches.map((g) => (
                <button
                  key={g.id}
                  onClick={() => onAddGlobal(g.id)}
                  style={{
                    textAlign: 'left',
                    border: '1px solid var(--teal)',
                    borderRadius: 8,
                    background: 'rgba(95,168,162,0.06)',
                    padding: '7px 10px',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <span style={{ fontWeight: 600, display: 'block' }}>{g.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {SECTION_META[g.section.type as SectionType]?.label ?? String(g.section.type)} · edit once in Shared blocks, updates everywhere
                  </span>
                </button>
              ))}
              <p style={{ margin: '6px 2px 2px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                Sections
              </p>
            </>
          )}
          {matches.map((t) => (
            <button
              key={t}
              onClick={() => onAdd(t)}
              style={{
                textAlign: 'left',
                border: '1px solid var(--line-soft)',
                borderRadius: 8,
                background: 'var(--card)',
                padding: '7px 10px',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 600, display: 'block' }}>{SECTION_META[t].label}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{SECTION_META[t].hint}</span>
            </button>
          ))}
          {matches.length === 0 && globalMatches.length === 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 2px' }}>
              No sections match "{q}".
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** New-page form. Module-level so parent re-renders don't remount it and
 * wipe what's typed. Start points: a page template (starter layout from
 * @vv/sections) or a copy of an existing page (server-side clone). */
function NewPageForm({
  createPage,
  clonePage,
  pages,
  onCancel,
  onCreated,
}: {
  createPage: (
    slug: string,
    title: string,
    description: string,
    sections?: Record<string, unknown>[],
  ) => Promise<{ ok: boolean; error?: string }>;
  clonePage: (
    from: string,
    to: string,
    title: string,
    description: string,
  ) => Promise<{ ok: boolean; error?: string }>;
  pages: string[];
  onCancel: () => void;
  onCreated: (slug: string) => void;
}) {
  const [pSlug, setPSlug] = useState('');
  const [pTitle, setPTitle] = useState('');
  const [pDesc, setPDesc] = useState('');
  const [start, setStart] = useState<string>('blank'); // template key | 'copy'
  const [copyFrom, setCopyFrom] = useState<string>(pages[0] ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cleanSlug = sanitizeSlug(pSlug);
  const inputStyle: React.CSSProperties = {
    width: '100%',
    fontSize: 13,
    padding: '7px 10px',
    border: '1px solid var(--line)',
    borderRadius: 8,
    boxSizing: 'border-box',
  };
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!cleanSlug || !pTitle.trim() || !pDesc.trim()) return;
        if (start === 'copy' && !copyFrom) return;
        setBusy(true);
        setError(null);
        let r: { ok: boolean; error?: string };
        if (start === 'copy') {
          r = await clonePage(copyFrom, cleanSlug, pTitle.trim(), pDesc.trim());
        } else {
          const template = PAGE_TEMPLATES.find((t) => t.key === start);
          r = await createPage(
            cleanSlug,
            pTitle.trim(),
            pDesc.trim(),
            template ? buildTemplateSections(template) : undefined,
          );
        }
        setBusy(false);
        if (r.ok) onCreated(cleanSlug);
        else setError(r.error ?? 'Could not create the page');
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 10,
        border: '1px solid var(--line)',
        borderRadius: 10,
        background: 'var(--surface)',
      }}
    >
      <input style={inputStyle} placeholder="slug (e.g. peptides)" value={pSlug} onChange={(e) => setPSlug(e.target.value)} />
      {cleanSlug && cleanSlug !== pSlug && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>Will be created as /{cleanSlug}</p>
      )}
      <input style={inputStyle} placeholder="Page title (browser tab + SEO)" value={pTitle} onChange={(e) => setPTitle(e.target.value)} />
      <input style={inputStyle} placeholder="Meta description (SEO)" value={pDesc} onChange={(e) => setPDesc(e.target.value)} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ margin: '2px 0 2px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          Start from
        </p>
        {PAGE_TEMPLATES.map((t) => (
          <label
            key={t.key}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'baseline',
              fontSize: 12.5,
              cursor: 'pointer',
              padding: '4px 6px',
              borderRadius: 6,
              background: start === t.key ? 'rgba(95,168,162,0.1)' : 'transparent',
            }}
          >
            <input
              type="radio"
              name="page-template"
              checked={start === t.key}
              onChange={() => setStart(t.key)}
            />
            <span>
              <span style={{ fontWeight: 600 }}>{t.label}</span>
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{t.hint}</span>
            </span>
          </label>
        ))}
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            fontSize: 12.5,
            cursor: 'pointer',
            padding: '4px 6px',
            borderRadius: 6,
            background: start === 'copy' ? 'rgba(95,168,162,0.1)' : 'transparent',
          }}
        >
          <input type="radio" name="page-template" checked={start === 'copy'} onChange={() => setStart('copy')} />
          <span style={{ flex: 1 }}>
            <span style={{ fontWeight: 600 }}>Copy of an existing page</span>
            {start === 'copy' && (
              <select
                value={copyFrom}
                onChange={(e) => setCopyFrom(e.target.value)}
                style={{ ...inputStyle, marginTop: 4 }}
              >
                {pages.map((p) => (
                  <option key={p} value={p}>
                    /{p === 'home' ? '' : p}
                  </option>
                ))}
              </select>
            )}
          </span>
        </label>
      </div>
      {error && (
        <p
          style={{
            margin: 0,
            padding: '6px 10px',
            background: 'rgba(220,80,80,0.08)',
            border: '1px solid rgba(220,80,80,0.3)',
            borderRadius: 8,
            color: '#c44545',
            fontSize: 12,
          }}
        >
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={busy} className="content-editor__save" style={{ cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Creating…' : 'Create page'}
        </button>
        <button type="button" className="content-editor__rebuild" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
        The page gets a route automatically on the next Publish.
      </p>
    </form>
  );
}

// ── Revision history ────────────────────────────────────────────────

interface RevisionSummary {
  id: string;
  author_email: string | null;
  cause: 'save' | 'pre-restore';
  created_at: string;
  section_count: number;
  types: string[];
}

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** History modal — every saved change to the page, restorable (dev-only:
 * a restore can change structure). Restoring snapshots the current state
 * first, so a restore is itself undoable from this same list. */
function HistoryPanel({
  slug,
  isDev,
  onClose,
  onRestored,
}: {
  slug: string;
  isDev: boolean;
  onClose: () => void;
  onRestored: () => void;
}) {
  const [revisions, setRevisions] = useState<RevisionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/admin/content/page/${encodeURIComponent(slug)}/revisions`,
          { headers: authHeaders(false) },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { revisions: RevisionSummary[] };
        if (!cancelled) setRevisions(data.revisions);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function restore(rev: RevisionSummary) {
    if (
      !window.confirm(
        `Roll /${slug} back to the version from ${timeAgo(rev.created_at)} (${rev.section_count} sections)? The current version is snapshotted first, so you can undo this from History.`,
      )
    )
      return;
    setBusyId(rev.id);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/content/page/${encodeURIComponent(slug)}/revisions/${encodeURIComponent(rev.id)}/restore`,
        { method: 'POST', headers: authHeaders() },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      onRestored();
    } catch (e) {
      setError((e as Error).message);
      setBusyId(null);
    }
  }

  return (
    <div className="pages-editor__palette-backdrop" onClick={onClose}>
      <div
        className="pages-editor__palette"
        role="dialog"
        aria-modal="true"
        aria-label="Page history"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            History — /{slug === 'home' ? '' : slug}
          </span>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}
            aria-label="Close history"
          >
            ×
          </button>
        </div>
        {error && (
          <p style={{ margin: 0, padding: '6px 10px', background: 'rgba(220,80,80,0.08)', border: '1px solid rgba(220,80,80,0.3)', borderRadius: 8, color: '#c44545', fontSize: 12 }}>
            {error}
          </p>
        )}
        <div className="pages-editor__palette-list">
          {revisions === null && !error && (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 2px' }}>Loading…</p>
          )}
          {revisions?.length === 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 2px' }}>
              No saved versions yet — history starts recording from the next edit.
            </p>
          )}
          {revisions?.map((rev) => (
            <div
              key={rev.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid var(--line-soft)',
                borderRadius: 8,
                background: 'var(--card)',
                padding: '8px 10px',
                fontSize: 12.5,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600, display: 'block' }}>
                  {timeAgo(rev.created_at)}
                  {rev.cause === 'pre-restore' && (
                    <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · before a restore</span>
                  )}
                </span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {rev.section_count} section{rev.section_count === 1 ? '' : 's'}
                  {rev.author_email ? ` · ${rev.author_email}` : ''}
                </span>
              </span>
              {isDev && (
                <button
                  onClick={() => void restore(rev)}
                  disabled={busyId !== null}
                  className="content-editor__rebuild"
                  style={{ cursor: busyId ? 'wait' : 'pointer', fontSize: 12 }}
                >
                  {busyId === rev.id ? 'Restoring…' : 'Restore'}
                </button>
              )}
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
          A version is kept for every saved change (newest 25). Restoring puts that version back
          in the editor — hit Publish to take it live.
        </p>
      </div>
    </div>
  );
}

// ── Main editor ─────────────────────────────────────────────────────

export default function PagesEditor() {
  const ed = usePageDocs();
  const gb = useGlobalBlocks();
  const lists = useLists();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pub = usePublish();
  const isDev = user?.role === 'dev';

  const globalsMap = useMemo(
    () => new Map(gb.blocks.map((b) => [b.id, b])),
    [gb.blocks],
  );

  const slugs = useMemo(() => Object.keys(ed.docs).sort(), [ed.docs]);
  const [initial] = useState(initialTarget);
  const [activeSlug, setActiveSlug] = useState<string>(initial.slug);
  /** Non-null = Lists mode: ListWorkspace owns the body. */
  const [activeList, setActiveList] = useState<ListKey | null>(initial.list);
  const [listSel, setListSel] = useState<string | null>(initial.item);
  /** Spots left by preview-click jumps (newest last) — "← Back" pops.
   * Deliberate navigation (picker, drawer) clears it. */
  const [backStack, setBackStack] = useState<Place[]>([]);
  const [sel, setSel] = useState<Sel>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drag, setDrag] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [dragPos, setDragPos] = useState<'above' | 'below'>('above');
  const [menuKey, setMenuKey] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  // Field focus handed to SectionForm — nonce makes re-clicks of the same
  // field re-trigger the scroll/flash.
  const [focusPath, setFocusPath] = useState<{ path: string; nonce: number } | null>(null);
  const focusSeq = useRef(0);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // Deployed-DOM section inventory from the preview's ready handshake. Held
  // in state (not applied inline) because the iframe can be ready before the
  // page docs finish loading — the effect below re-applies once they have.
  const [inventory, setInventory] = useState<{
    slug: string;
    sections: PreviewSectionInfo[];
  } | null>(null);

  const doc: PageDoc | undefined = ed.docs[activeSlug] ?? ed.docs[slugs[0] ?? ''];
  const slug = doc?.slug ?? activeSlug;

  // ── Review (Comment mode) ───────────────────────────────────────
  // A thread is a numbered pin left on this page at one screen size. The
  // inbox and the digest email deep-link here with ?review=<id>, which opens
  // Comment mode on that pin. docs/REVIEW.md has the whole flow.
  const [initialReview] = useState(() => new URLSearchParams(window.location.search).get('review'));
  const [mode, setMode] = useState<'edit' | 'comment'>(initialReview ? 'comment' : 'edit');
  const commenting = mode === 'comment' && !activeList;
  const review = useReview(slug);
  const [previewWidth, setPreviewWidth] = useState<PreviewWidth>('desktop');
  const [reviewDraft, setReviewDraft] = useState<ReviewDraft | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(initialReview);
  const [pinFocus, setPinFocus] = useState<{ id: string; nonce: number } | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const pinFocusSeq = useRef(0);
  const deepLinked = useRef(false);

  /** Make a thread active and scroll the preview to its pin. */
  function focusThread(id: string) {
    pinFocusSeq.current += 1;
    setActiveThreadId(id);
    setPinFocus({ id, nonce: pinFocusSeq.current });
  }

  /** Open a thread from the panel: switch to the screen size it was left
   * on (its pin only exists there), then scroll to it. */
  function openThread(id: string | null) {
    setReviewDraft(null);
    if (!id) {
      setActiveThreadId(null);
      return;
    }
    const t = review.threads.find((x) => x.id === id);
    if (t && t.viewport !== previewWidth) setPreviewWidth(t.viewport);
    focusThread(id);
  }

  // Deep link (?review=<id>): once the page's threads load, land on that pin.
  useEffect(() => {
    if (!initialReview || deepLinked.current) return;
    const t = review.threads.find((x) => x.id === initialReview);
    if (!t) return;
    deepLinked.current = true;
    setPreviewWidth(t.viewport);
    focusThread(t.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review.threads]);

  // Only pins from THIS screen size draw (their positions belong to it).
  const pins: PreviewPin[] = useMemo(
    () =>
      review.threads
        .filter(
          (t) =>
            t.anchor &&
            t.viewport === previewWidth &&
            (t.status === 'open' || showResolved || t.id === activeThreadId),
        )
        .map((t) => ({
          id: t.id,
          label: pinLabel(t),
          kind: t.kind,
          status: t.status,
          anchor: t.anchor!,
        })),
    [review.threads, previewWidth, showResolved, activeThreadId],
  );
  const openOnPage = review.threads.filter((t) => t.status === 'open').length;

  function enterComment() {
    setSel(null);
    setPaletteOpen(false);
    setMode('comment');
  }

  function enterEdit() {
    setReviewDraft(null);
    setPinFocus(null); // a pin focus must not outlive Comment mode
    setMode('edit');
  }

  /** "Edit this section →" on a comment: back to Edit mode, section open. */
  function editSectionOf(t: ReviewThread) {
    const a = t.anchor?.section;
    if (!a || !doc) return;
    const sameType = doc.sections
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.data.type === a.type);
    const match =
      doc.sections.find((s) => s.originalId === a.id && s.data.type === a.type) ??
      sameType.sort((x, y) => Math.abs(x.i - a.index) - Math.abs(y.i - a.index))[0]?.s;
    enterEdit();
    if (match) selectSection(match.key);
  }

  function selectPage(next: string, keepBack = false) {
    setActiveSlug(next);
    setActiveList(null);
    setListSel(null);
    setReviewDraft(null);
    setActiveThreadId(null);
    setPinFocus(null);
    setSel(null);
    setFocusPath(null);
    setPaletteOpen(false);
    setMenuKey(null);
    if (!keepBack) setBackStack([]);
    window.history.replaceState(null, '', window.location.pathname + '#' + encodeURIComponent(next));
  }

  function selectList(next: ListKey, item: string | null = null, keepBack = false) {
    setActiveList(next);
    setListSel(item);
    setMode('edit');
    setReviewDraft(null);
    setPinFocus(null);
    setSel(null);
    setFocusPath(null);
    setPaletteOpen(false);
    setHistoryOpen(false);
    setMenuKey(null);
    if (!keepBack) setBackStack([]);
    window.history.replaceState(null, '', `${window.location.pathname}#list:${next}`);
  }

  /** Drawer links into this editor ("/pages", "/pages?list=faqs"). */
  function pickEditorLink(to: string) {
    const list = new URLSearchParams(to.split('?')[1] ?? '').get('list');
    if (isListKey(list)) selectList(list);
    else selectPage(slug);
  }

  function selectSection(key: string) {
    setSel({ kind: 'section', key });
    setFocusPath(null);
  }

  // ── Click-anything navigation ───────────────────────────────────
  // Every card and section in a preview is clickable wherever you are:
  // a card opens in its list, anything else opens its section on the
  // page. These jumps remember where they came from for "← Back".
  function currentPlace(): Place {
    return activeList
      ? { kind: 'list', list: activeList, item: listSel }
      : { kind: 'page', slug, sectionKey: sel?.kind === 'section' ? sel.key : null };
  }

  function goTo(place: Place) {
    if (place.kind === 'list') {
      selectList(place.list, place.item, true);
    } else {
      selectPage(place.slug, true);
      if (place.sectionKey) setSel({ kind: 'section', key: place.sectionKey });
    }
  }

  function jump(to: Place, from: Place = currentPlace()) {
    goTo(to);
    setBackStack((s) => [...s.slice(-9), from]);
  }

  /** A section clicked in a LIST preview → that section on its page. */
  function jumpToSection(pageSlug: string, target: string, field?: string) {
    const match = ed.docs[pageSlug]?.sections.find((s) => s.originalId === target);
    if (!ed.docs[pageSlug]) return;
    jump({ kind: 'page', slug: pageSlug, sectionKey: match?.key ?? null });
    if (match && field) {
      focusSeq.current += 1;
      setFocusPath({ path: field, nonce: focusSeq.current });
    }
  }

  function goBack() {
    const place = backStack[backStack.length - 1];
    if (!place) return;
    setBackStack((s) => s.slice(0, -1));
    goTo(place);
  }

  /** A nav/footer link clicked in a preview → that page, if it's one this
   * editor builds (static pages like /privacy aren't). */
  function openPath(path: string) {
    const target = path.split('/').filter(Boolean)[0] ?? 'home';
    if (!ed.docs[target] || (!activeList && target === slug)) return;
    jump({ kind: 'page', slug: target, sectionKey: null });
  }

  /** Nav/footer text (not a link) clicked → where site chrome is edited. */
  function openChrome() {
    navigate('/navigation');
  }

  function placeLabel(place: Place): string {
    if (place.kind === 'list') return LIST_META[place.list].label;
    return (ed.docs[place.slug]?.meta.title ?? place.slug).split('—')[0]!.trim();
  }

  // Align preview ids with the deployed DOM (heals the id drift that made
  // clicks select the wrong section after unpublished layout changes).
  const rebindPreviewIds = ed.rebindPreviewIds;
  useEffect(() => {
    if (!inventory || ed.loading) return;
    rebindPreviewIds(inventory.slug, inventory.sections);
  }, [inventory, ed.loading, rebindPreviewIds]);

  // The pending-publish count lags in-page autosaves without this — the
  // topbar poll alone is 30s behind.
  const refreshPending = pub.refreshPending;
  useEffect(() => {
    if (ed.lastSavedAt || lists.lastSavedAt) void refreshPending();
  }, [ed.lastSavedAt, lists.lastSavedAt, refreshPending]);

  // Escape: drawer > palette > history > row menu > props panel — but never
  // while the user is typing in a field.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape' || isTypingTarget()) return;
      if (drawerOpen) setDrawerOpen(false);
      else if (paletteOpen) setPaletteOpen(false);
      else if (historyOpen) setHistoryOpen(false);
      else if (menuKey) setMenuKey(null);
      else if (sel) setSel(null);
      else if (listSel) setListSel(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, paletteOpen, historyOpen, menuKey, sel, listSel]);

  // A record-bound section's panel shows its list's items — load them.
  const selectedListLink = useMemo(() => {
    if (!doc || sel?.kind !== 'section') return null;
    const s = doc.sections.find((x) => x.key === sel.key);
    return s ? listForSection(s.data) : null;
  }, [doc, sel]);
  // Keyed on the list NAME — listForSection() returns a fresh object on
  // every edit of the section's own fields.
  const selectedLinkList = selectedListLink?.list ?? null;
  const ensureListLoaded = lists.ensureLoaded;
  useEffect(() => {
    if (selectedLinkList) void ensureListLoaded(selectedLinkList);
  }, [selectedLinkList, ensureListLoaded]);

  // Row ⋯ menu closes on any outside click.
  useEffect(() => {
    if (!menuKey) return;
    function onClick() {
      setMenuKey(null);
    }
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, [menuKey]);

  const selectedSection = useMemo(() => {
    if (!doc || !sel || sel.kind !== 'section') return null;
    return doc.sections.find((s) => s.key === sel.key) ?? null;
  }, [doc, sel]);

  // Keep the selected row visible when selection arrives from the preview.
  useEffect(() => {
    if (sel?.kind !== 'section') return;
    rowRefs.current.get(sel.key)?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  // ── Preview wiring ──────────────────────────────────────────────
  // Section drafts for this page + (below) every list's card drafts, so the
  // preview shows ALL unpublished edits at once. Hidden sections are left
  // out of the order, which hides them live; it MAY be [] (hide every one).
  const { sectionFields, sectionOrder } = useMemo(() => sectionPatches(doc ?? undefined), [doc]);

  // Cards/FAQs/steps edited in Lists mode are saved but unpublished, so the
  // deployed page doesn't show them — load every list and overlay them here
  // too, or section edits and card edits would only ever appear one at a time.
  useEffect(() => {
    for (const k of LIST_KEYS) void ensureListLoaded(k);
  }, [ensureListLoaded]);
  const { recordFields, recordOrder } = useMemo(
    () => recordPatches(lists),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lists.records, lists.loadedRecord],
  );

  const structureChanged = useMemo(
    () => (doc?.sections ?? []).some((s) => s.originalId === null),
    [doc],
  );

  const selectedSelector =
    selectedSection?.originalId != null
      ? `[data-section="${selectedSection.originalId}"]`
      : null;

  const hoverSelector = hoverId ? `[data-section="${hoverId}"]` : null;

  // ── Status chip ─────────────────────────────────────────────────
  const issues = ed.issuesBySlug[slug] ?? [];
  function statusChip() {
    const failedCount = ed.failedCount + lists.failedCount;
    if (failedCount > 0) {
      return (
        <button
          onClick={() => {
            ed.retryFailed();
            lists.retryFailed();
          }}
          title={ed.failReason ?? lists.failReason ?? undefined}
          style={{
            background: 'rgba(220,80,80,0.1)',
            border: '1px solid rgba(220,80,80,0.4)',
            color: '#c44545',
            borderRadius: 999,
            padding: '3px 10px',
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          ⚠ {failedCount} not saved — retry
        </button>
      );
    }
    // Record drafts blocked by validation, per list.
    const blockedLists = [
      ...new Set(Object.keys(lists.issues).map((k) => k.slice(0, k.indexOf(':')))),
    ].filter(isListKey);
    if (activeList && blockedLists.includes(activeList)) {
      const n = Object.keys(lists.issues).filter((k) => k.startsWith(`${activeList}:`)).length;
      return (
        <span style={{ color: '#c44545', fontSize: 12, whiteSpace: 'nowrap' }}>
          ⚠ fix {n} item{n === 1 ? '' : 's'} to save
        </span>
      );
    }
    if (!activeList && issues.length > 0) {
      return (
        <span style={{ color: '#c44545', fontSize: 12, whiteSpace: 'nowrap' }}>
          ⚠ fix {issues.length} issue{issues.length === 1 ? '' : 's'} to save
        </span>
      );
    }
    const blockedList = blockedLists.find((l) => l !== activeList);
    if (blockedList) {
      return (
        <button
          onClick={() => selectList(blockedList)}
          style={{
            background: 'transparent',
            border: '1px solid rgba(220,80,80,0.4)',
            color: '#c44545',
            borderRadius: 999,
            padding: '3px 10px',
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          ⚠ fix issues in {LIST_META[blockedList].label}
        </button>
      );
    }
    // Another page blocked by validation never flushes and stays dirty —
    // point at it instead of showing a forever-"Saving…".
    const blockedOther = Object.keys(ed.issuesBySlug).find(
      (s2) => (activeList !== null || s2 !== slug) && ed.dirtySlugs.has(s2),
    );
    if (blockedOther) {
      return (
        <button
          onClick={() => selectPage(blockedOther)}
          style={{
            background: 'transparent',
            border: '1px solid rgba(220,80,80,0.4)',
            color: '#c44545',
            borderRadius: 999,
            padding: '3px 10px',
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          ⚠ fix issues on /{blockedOther}
        </button>
      );
    }
    const savingForReal =
      ed.saving || [...ed.dirtySlugs].some((s2) => !(ed.issuesBySlug[s2]?.length)) || lists.saving;
    if (savingForReal) {
      return <span style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>Saving…</span>;
    }
    if (ed.lastSavedAt || lists.lastSavedAt) {
      return <span style={{ color: 'var(--teal)', fontSize: 12, whiteSpace: 'nowrap' }}>✓ All changes saved</span>;
    }
    return null;
  }

  // ── Section list rows ───────────────────────────────────────────
  function renderSectionRow(s: SectionDraft, i: number) {
    const { type, snippet } = sectionRowLabel(s, globalsMap);
    const isSelected = sel?.kind === 'section' && sel.key === s.key;
    const isNew = s.originalId === null;
    const isHidden = s.data.hidden === true;
    const isDragOver = dragOverKey === s.key;
    return (
      <div
        key={s.key}
        ref={(el) => {
          if (el) rowRefs.current.set(s.key, el);
          else rowRefs.current.delete(s.key);
        }}
        draggable={isDev}
        onDragStart={(e) => {
          if (!isDev) return;
          setDrag(s.key);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', s.key);
        }}
        onDragOver={(e) => {
          if (!isDev || !drag || drag === s.key) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const rect = e.currentTarget.getBoundingClientRect();
          setDragOverKey(s.key);
          setDragPos(e.clientY < rect.top + rect.height / 2 ? 'above' : 'below');
        }}
        onDragLeave={() => {
          if (dragOverKey === s.key) setDragOverKey(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isDev && drag && drag !== s.key && doc) {
            const fromIdx = doc.sections.findIndex((x) => x.key === drag);
            const targetIdx = doc.sections.findIndex((x) => x.key === s.key);
            if (fromIdx >= 0 && targetIdx >= 0) {
              // Insert position in the PRE-removal array, then compensate
              // for the drag item being spliced out first (moveSection
              // removes, THEN inserts) — dragging downward was off by one.
              let insertIdx = targetIdx + (dragPos === 'below' ? 1 : 0);
              if (fromIdx < insertIdx) insertIdx -= 1;
              ed.moveSection(slug, drag, insertIdx);
            }
          }
          setDrag(null);
          setDragOverKey(null);
        }}
        onDragEnd={() => {
          setDrag(null);
          setDragOverKey(null);
        }}
        onClick={() => selectSection(s.key)}
        onMouseEnter={() => {
          if (s.originalId) setHoverId(s.originalId);
        }}
        onMouseLeave={() => {
          setHoverId((cur) => (cur === s.originalId ? null : cur));
        }}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 8,
          border: isSelected ? '1px solid var(--teal)' : '1px solid var(--line-soft)',
          // Drop indicator as an inset shadow — toggling borderTop/Bottom
          // next to the `border` shorthand makes React warn on every drag.
          boxShadow:
            isDragOver && dragPos === 'above'
              ? 'inset 0 2px 0 var(--teal)'
              : isDragOver && dragPos === 'below'
                ? 'inset 0 -2px 0 var(--teal)'
                : undefined,
          background: isSelected ? 'rgba(95,168,162,0.12)' : 'var(--card)',
          opacity: drag === s.key ? 0.35 : isHidden ? 0.55 : 1,
          cursor: 'pointer',
          userSelect: 'none',
          fontSize: 13.5,
          fontWeight: isSelected ? 600 : 400,
          transition: 'background 120ms, border-color 120ms, opacity 120ms',
        }}
      >
        {isDev && (
          <span style={{ cursor: 'grab', opacity: 0.5 }} aria-hidden title="Drag to reorder">
            ⠿
          </span>
        )}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block' }}>{type}</span>
          {snippet && (
            <span
              style={{
                display: 'block',
                fontSize: 11.5,
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 400,
              }}
            >
              {snippet}
            </span>
          )}
        </span>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {isHidden && (
            <span title="Hidden — not rendered on the live site" style={{ color: 'var(--text-muted)', display: 'inline-flex' }}>
              <EyeOffIcon />
            </span>
          )}
          <span style={{ color: 'var(--text-muted)', fontSize: 10.5 }}>{i + 1}</span>
          {isNew && (
            <span
              title="New this session — appears on the live preview after Publish"
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: 'var(--teal)',
              }}
            >
              NEW
            </span>
          )}
          {isDev && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenuKey((cur) => (cur === s.key ? null : s.key));
              }}
              aria-label={`Section actions for ${type}`}
              aria-expanded={menuKey === s.key}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                fontSize: 15,
                lineHeight: 1,
                padding: '2px 4px',
                borderRadius: 4,
              }}
            >
              ⋯
            </button>
          )}
        </span>
        {isDev && menuKey === s.key && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              right: 8,
              top: '100%',
              zIndex: 30,
              minWidth: 160,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: 4,
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              boxShadow: 'var(--sh-2)',
            }}
          >
            {[
              {
                label: 'Duplicate',
                onPick: () => {
                  const key = ed.duplicateSection(slug, s.key);
                  if (key) selectSection(key);
                },
              },
              {
                label: isHidden ? 'Show section' : 'Hide section',
                onPick: () => {
                  ed.setSectionData(slug, s.key, { ...s.data, hidden: !isHidden });
                },
              },
              {
                label: 'Delete…',
                danger: true,
                onPick: () => {
                  if (!window.confirm(`Delete this ${type} section? This can't be undone.`)) return;
                  ed.removeSection(slug, s.key);
                  if (isSelected) setSel(null);
                },
              },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  setMenuKey(null);
                  item.onPick();
                }}
                style={{
                  textAlign: 'left',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 6,
                  fontSize: 12.5,
                  color: item.danger ? '#c44545' : 'var(--text)',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Record-bound section → its list ─────────────────────────────
  /** Card on a record-bound section's panel: the items aren't in this
   * form, they're a list — say which, how many show here, and jump to it
   * (clicking a card in the preview opens that card directly). */
  function renderListLink(data: Record<string, unknown>) {
    const link = listForSection(data);
    if (!link) return null;
    const lmeta = LIST_META[link.list];
    const ldef = LIST_DEFS[link.list];
    const rows = lists.records[link.list];
    const shown = rows?.filter(
      (r) =>
        r.is_active &&
        (link.group === undefined || r.page === link.group) &&
        (!ldef.onPage || ldef.onPage(r)),
    );
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '12px 14px',
          border: '1px solid var(--teal)',
          borderRadius: 10,
          background: 'rgba(95,168,162,0.06)',
        }}
      >
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
          The {lmeta.itemLabel}s in this section come from the <strong>{lmeta.label}</strong> list
          {link.group ? ` (${link.group} group)` : ''}
          {shown ? ` — ${shown.length} shown here` : ''}. This form only sets the heading around
          them — click any {lmeta.itemLabel} in the preview to edit it.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => jump({ kind: 'list', list: link.list, item: null })}
            style={{
              background: 'var(--primary)',
              color: 'var(--card)',
              border: 'none',
              borderRadius: 8,
              padding: '7px 12px',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Edit {lmeta.label} →
          </button>
        </div>
      </div>
    );
  }

  // ── Props panel ─────────────────────────────────────────────────
  function panelShell(title: string, subtitle: string, body: React.ReactNode, footer?: React.ReactNode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            padding: '14px 16px',
            borderBottom: '1px solid var(--line-soft)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>
              {title}
            </div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              {subtitle}
            </div>
          </div>
          <button
            onClick={() => setSel(null)}
            title="Close (Esc)"
            style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            ×
          </button>
        </div>
        {issues.length > 0 && (
          <div
            style={{
              margin: '10px 14px 0',
              padding: '8px 12px',
              background: 'rgba(220,80,80,0.08)',
              border: '1px solid rgba(220,80,80,0.3)',
              borderRadius: 8,
              fontSize: 12,
              color: '#c44545',
            }}
          >
            <strong>Not saving yet — fix:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {issues.slice(0, 5).map((iss, i) => (
                <li key={i}>
                  <code style={{ fontSize: 11 }}>{iss.path || 'page'}</code>: {iss.message}
                </li>
              ))}
              {issues.length > 5 && <li>…and {issues.length - 5} more</li>}
            </ul>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {body}
        </div>
        {footer && <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line-soft)' }}>{footer}</div>}
      </div>
    );
  }

  function renderPanel() {
    if (!doc || !sel) return null;

    if (sel.kind === 'meta') {
      const meta = doc.meta;
      const setMetaField = (k: string, v: unknown) => {
        const next = { ...meta } as Record<string, unknown>;
        if (v === undefined || v === '') delete next[k];
        else next[k] = v;
        ed.setMeta(slug, next as typeof meta);
      };
      const inputStyle: React.CSSProperties = {
        width: '100%',
        fontSize: 14,
        padding: '10px 12px',
        border: '1px solid var(--line)',
        borderRadius: 8,
        boxSizing: 'border-box',
      };
      const labelStyle: React.CSSProperties = {
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--primary)',
        display: 'block',
        marginBottom: 4,
      };
      return panelShell(
        'Page settings',
        `/${slug === 'home' ? '' : slug}`,
        <>
          <div>
            <label style={labelStyle}>Title (browser tab + SEO)</label>
            <input style={inputStyle} value={meta.title ?? ''} onChange={(e) => setMetaField('title', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Meta description</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical' }}
              rows={3}
              value={meta.description ?? ''}
              onChange={(e) => setMetaField('description', e.target.value)}
            />
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              What search engines + AI engines show. 150-160 characters is the sweet spot.
            </p>
          </div>
          <div>
            <label style={labelStyle}>Social share image (OG)</label>
            <ImagePicker
              value={typeof meta.ogImage === 'string' ? meta.ogImage : ''}
              onChange={(next) => setMetaField('ogImage', next || undefined)}
              aspect="1200/630"
            />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={Boolean(meta.noindex)}
              onChange={(e) => setMetaField('noindex', e.target.checked || undefined)}
            />
            Hide from search engines (noindex)
          </label>
        </>,
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0 }}>
            Saves automatically. Hit Publish (top right) to put changes live.
          </p>
          {isDev && slug !== 'home' && (
            <button
              onClick={async () => {
                if (!window.confirm(`Delete the "${slug}" page entirely? This can't be undone.`)) return;
                const ok = await ed.deletePage(slug);
                if (ok) selectPage('home');
              }}
              style={{
                background: 'transparent',
                border: '1px solid rgba(196,69,69,0.4)',
                color: '#c44545',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Delete page
            </button>
          )}
        </div>,
      );
    }

    const s = selectedSection;
    if (!s) return null;

    // Global refs: the payload lives in the Blocks library, not on this
    // page — show what it is and where to edit it instead of a form whose
    // edits would silently go nowhere.
    if (s.data.type === 'global') {
      const block = globalsMap.get(String(s.data.blockId ?? ''));
      return panelShell(
        block ? block.name : 'Shared block',
        `${slug} page · shared section`,
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {block ? (
            <>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
                This is the shared <strong>{SECTION_META[block.section.type as SectionType]?.label ?? String(block.section.type)}</strong>{' '}
                block <strong>“{block.name}”</strong>. Its content is edited once, in Shared
                blocks — every page using it updates together on the next Publish.
              </p>
              {block.usedBy.length > 0 && (
                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                  Also on: {block.usedBy.filter((p) => p !== slug).map((p) => `/${p}`).join(', ') || 'no other pages yet'}
                </p>
              )}
              <NavLink
                to="/blocks"
                style={{
                  alignSelf: 'flex-start',
                  background: 'var(--primary)',
                  color: 'var(--card)',
                  borderRadius: 8,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Edit in Shared blocks →
              </NavLink>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: '#c44545' }}>
              The shared block this section points to no longer exists — it renders nothing on the
              live site. Delete this section, or recreate the block in Shared blocks.
            </p>
          )}
        </div>,
        isDev ? (
          <button
            onClick={() => {
              if (!window.confirm('Remove this shared block from this page? The block itself stays in the library.')) return;
              ed.removeSection(slug, s.key);
              setSel(null);
            }}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid rgba(196,69,69,0.4)',
              color: '#c44545',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12.5,
              cursor: 'pointer',
            }}
          >
            Remove from this page
          </button>
        ) : undefined,
      );
    }

    const meta = SECTION_META[s.data.type as SectionType];
    return panelShell(
      meta?.label ?? String(s.data.type),
      `${slug} page${s.originalId === null ? ' · new — publishes on next Publish' : ''}`,
      <>
        {renderListLink(s.data)}
        <SectionForm
          // Keyed per section so switching between two sections of the SAME
          // type remounts the form — otherwise ItemList open-state (and any
          // other internal state) leaks from the previous section.
          key={s.key}
          type={s.data.type as SectionType}
          value={s.data}
          onChange={(next) => ed.setSectionData(slug, s.key, next)}
          focusPath={focusPath}
        />
      </>,
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0 }}>
          Text + photo edits preview instantly and save automatically. Hit Publish (top right) to
          put them live.
        </p>
        {isDev && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                const key = ed.duplicateSection(slug, s.key);
                if (key) selectSection(key);
              }}
              className="content-editor__rebuild"
              style={{ flex: 1 }}
            >
              Duplicate
            </button>
            <button
              onClick={() => {
                const { type } = sectionRowLabel(s, globalsMap);
                if (!window.confirm(`Delete this ${type} section? This can't be undone.`)) return;
                ed.removeSection(slug, s.key);
                setSel(null);
              }}
              style={{
                flex: 1,
                background: 'transparent',
                border: '1px solid rgba(196,69,69,0.4)',
                color: '#c44545',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              Delete section
            </button>
          </div>
        )}
      </div>,
    );
  }

  // ── Topbar publish button ───────────────────────────────────────
  function renderPublish() {
    // Editors can't deploy (the API 403s POST /publish) — don't show a
    // button that can only fail.
    if (user?.role === 'editor') return null;
    return (
      <>
        {pub.msg && (
          <span
            style={{
              fontSize: 12,
              color: pub.state === 'error' ? '#c44545' : 'var(--text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 260,
            }}
            title={pub.msg}
          >
            {pub.msg}
          </span>
        )}
        <button
          type="button"
          onClick={() => void pub.publish()}
          // 'done' is disabled too — an accidental click on '✓ Published'
          // must not fire a second deploy.
          disabled={
            pub.state === 'publishing' ||
            pub.state === 'done' ||
            (pub.state === 'idle' && pub.pendingCount === 0)
          }
          title={
            pub.pendingCount === 0
              ? 'No pending content changes'
              : `Push ${pub.pendingCount ?? 'all saved'} content edit${pub.pendingCount === 1 ? '' : 's'} to the live site (~30-60s rebuild)`
          }
          style={{
            background: pub.state === 'done' || pub.pendingCount === 0 ? 'transparent' : 'var(--primary)',
            color: pub.state === 'done' || pub.pendingCount === 0 ? 'var(--text-muted)' : 'var(--card)',
            border: `1px solid ${pub.pendingCount === 0 ? 'var(--line)' : 'var(--primary)'}`,
            padding: '6px 14px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: pub.state === 'publishing' ? 'wait' :
                    pub.state === 'done' || pub.pendingCount === 0 ? 'default' : 'pointer',
            whiteSpace: 'nowrap',
            transition: 'background 120ms, color 120ms',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
          }}
        >
          {pub.state === 'publishing' ? 'Publishing…' :
           pub.state === 'done' ? '✓ Published' :
           pub.pendingCount === 0 ? 'All published' :
           'Publish'}
          {pub.state === 'idle' && pub.pendingCount !== null && pub.pendingCount > 0 && (
            <span
              style={{
                background: 'rgba(255,255,255,0.22)',
                color: 'inherit',
                padding: '1px 7px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {pub.pendingCount}
            </span>
          )}
        </button>
      </>
    );
  }

  // ── Render ──────────────────────────────────────────────────────
  if (ed.loading) {
    return (
      <div className="pages-editor">
        <p style={{ margin: 'auto', color: 'var(--text-muted)' }}>Loading pages…</p>
      </div>
    );
  }

  return (
    <div className="pages-editor">
      <header className="pages-editor__topbar">
        <div className="pages-editor__topbar-side">
          <button
            type="button"
            className="pages-editor__burger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
          >
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" aria-hidden>
              <line x1="2.5" y1="4" x2="13.5" y2="4" />
              <line x1="2.5" y1="8" x2="13.5" y2="8" />
              <line x1="2.5" y1="12" x2="13.5" y2="12" />
            </svg>
          </button>
          <div className="pages-editor__wordmark">
            Valley Vitality <span>— Editor</span>
          </div>
        </div>

        <div className="pages-editor__topbar-center">
          {backStack.length > 0 && (
            <button
              type="button"
              onClick={goBack}
              title="Back to where you clicked from"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--teal)',
                whiteSpace: 'nowrap',
                padding: 0,
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              ← {placeLabel(backStack[backStack.length - 1]!)}
            </button>
          )}
          <select
            value={activeList ? `list:${activeList}` : `page:${slug}`}
            onChange={(e) => {
              const [kind, key] = e.target.value.split(/:(.*)/s) as [string, string];
              if (kind === 'list' && isListKey(key)) selectList(key);
              else selectPage(key);
            }}
            aria-label="Page or list to edit"
            style={{
              fontSize: 13.5,
              padding: '7px 10px',
              border: '1px solid var(--line)',
              borderRadius: 8,
              background: 'var(--card)',
              maxWidth: 300,
            }}
          >
            <optgroup label="Pages">
              {slugs.map((sl) => (
                <option key={sl} value={`page:${sl}`}>
                  {(ed.docs[sl]?.meta.title ?? sl).split('—')[0]!.trim()} — /{sl === 'home' ? '' : sl}
                </option>
              ))}
            </optgroup>
            <optgroup label="Lists">
              {LIST_KEYS.map((k) => (
                <option key={k} value={`list:${k}`}>
                  {LIST_META[k].label}
                </option>
              ))}
            </optgroup>
          </select>
          {statusChip()}
          {!activeList && (
            <div className="rv-mode" role="group" aria-label="Editor mode">
              <button type="button" aria-pressed={!commenting} onClick={enterEdit}>
                Edit
              </button>
              <button
                type="button"
                aria-pressed={commenting}
                onClick={enterComment}
                title="Click on the page to leave a numbered comment"
              >
                Comment
                {openOnPage > 0 && <span className="rv-count">{openOnPage}</span>}
              </button>
            </div>
          )}
        </div>

        <div className="pages-editor__topbar-side pages-editor__topbar-side--right">
          {!activeList && (
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              title="Saved versions of this page"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
                padding: 0,
              }}
            >
              ⟲ History
            </button>
          )}
          <a
            href={`${SITE_ORIGIN}${activeList ? defaultListHref(activeList, ed.docs) : hrefForSlug(slug)}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--teal)', whiteSpace: 'nowrap' }}
          >
            View live ↗
          </a>
          {renderPublish()}
        </div>
      </header>

      <p className="pages-editor__narrow-note">
        The editor works best on a wide screen — panes stack on narrow windows.
      </p>

      {activeList ? (
        <ListWorkspace
          list={activeList}
          lists={lists}
          docs={ed.docs}
          selectedId={listSel}
          onSelect={setListSel}
          onOpenList={(l, item) => jump({ kind: 'list', list: l, item })}
          onOpenSection={jumpToSection}
          onNavigate={openPath}
          onOpenChrome={openChrome}
        />
      ) : (
      <div
        className={`pages-editor__body${sel || commenting ? ' has-selection' : ''}${commenting ? ' is-commenting' : ''}`}
      >
        <nav className="pages-editor__nav">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {ed.loadError && (
              <div className="content-editor__error" style={{ padding: '8px 12px' }}>
                {ed.loadError}
              </div>
            )}

            {isDev &&
              (newPageOpen ? (
                <NewPageForm
                  createPage={ed.createPage}
                  clonePage={ed.clonePage}
                  pages={slugs}
                  onCancel={() => setNewPageOpen(false)}
                  onCreated={(created) => {
                    setNewPageOpen(false);
                    selectPage(created);
                  }}
                />
              ) : (
                <button
                  onClick={() => setNewPageOpen(true)}
                  style={{
                    background: 'transparent',
                    border: '1px dashed var(--line)',
                    color: 'var(--teal)',
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: '7px 10px',
                    fontSize: 12.5,
                    cursor: 'pointer',
                  }}
                >
                  + New page
                </button>
              ))}

            <button
              onClick={() => {
                setSel({ kind: 'meta' });
                setFocusPath(null);
              }}
              style={{
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 10px',
                borderRadius: 8,
                border:
                  sel?.kind === 'meta' ? '1px solid var(--teal)' : '1px solid var(--line-soft)',
                background: sel?.kind === 'meta' ? 'rgba(95,168,162,0.12)' : 'var(--card)',
                cursor: 'pointer',
                fontSize: 13.5,
                fontWeight: sel?.kind === 'meta' ? 600 : 400,
              }}
            >
              <span>⚙ Page settings</span>
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>SEO</span>
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {doc?.sections.map((s, i) => renderSectionRow(s, i))}
            </div>

            {isDev ? (
              <button
                onClick={() => setPaletteOpen(true)}
                style={{
                  background: 'transparent',
                  border: '1px dashed var(--line)',
                  color: 'var(--teal)',
                  fontWeight: 600,
                  borderRadius: 8,
                  padding: '9px 10px',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                + Add section
              </button>
            ) : (
              <p style={{ margin: '2px 4px', fontSize: 11.5, color: 'var(--text-muted)' }}>
                Layout is managed by HDM.
              </p>
            )}
          </div>
        </nav>

        <div className="pages-editor__preview">
          {doc && (
            <PagePreview
              href={hrefForSlug(slug)}
              sectionFields={sectionFields}
              sectionOrder={sectionOrder}
              recordFields={recordFields}
              recordOrder={recordOrder}
              scrollTo={commenting ? undefined : (selectedSelector ?? undefined)}
              selectedSelector={commenting ? null : selectedSelector}
              hoverSelector={commenting ? null : hoverSelector}
              structureChanged={structureChanged}
              onInventory={(sections) => setInventory({ slug, sections })}
              onNavigate={openPath}
              width={previewWidth}
              onWidthChange={(w) => {
                setPreviewWidth(w);
                setReviewDraft(null);
              }}
              commentMode={commenting}
              pins={pins}
              activePinId={activeThreadId}
              draftAnchor={reviewDraft?.anchor ?? null}
              focusPin={pinFocus}
              onCommentClick={(c) => {
                setActiveThreadId(null);
                setReviewDraft({
                  anchor: c.anchor,
                  viewport: previewWidth,
                  viewportWidth: c.viewportWidth,
                });
              }}
              onPinClick={(id) => {
                setReviewDraft(null);
                setActiveThreadId(id);
              }}
              notice={
                commenting ? (
                  <>
                    <strong>Comment mode</strong> · you’re on the{' '}
                    <strong>{VIEWPORT_LABEL[previewWidth]}</strong> view. Click anywhere to drop a
                    numbered pin — switch the screen size above to comment on another.
                  </>
                ) : undefined
              }
              onEditClick={(payload) => {
                if (payload.kind === 'chrome') {
                  openChrome();
                  return;
                }
                const match = doc.sections.find((s) => s.originalId === payload.target);
                // A card opens in its list; "← Back" returns here with the
                // card's section selected.
                if (payload.kind === 'record' && payload.record) {
                  const i = payload.record.indexOf(':');
                  const recList = payload.record.slice(0, i);
                  if (isListKey(recList)) {
                    jump(
                      { kind: 'list', list: recList, item: payload.record.slice(i + 1) },
                      { kind: 'page', slug, sectionKey: match?.key ?? null },
                    );
                    return;
                  }
                }
                if (!match) return;
                setSel({ kind: 'section', key: match.key });
                if (payload.kind === 'field' && payload.field) {
                  focusSeq.current += 1;
                  setFocusPath({ path: payload.field, nonce: focusSeq.current });
                } else {
                  setFocusPath(null);
                }
              }}
            />
          )}
        </div>

        <aside className="pages-editor__props" aria-hidden={!sel && !commenting}>
          {commenting ? (
            user && (
              <ReviewPanel
                viewport={previewWidth}
                threads={review.threads}
                draft={reviewDraft}
                activeId={activeThreadId}
                showResolved={showResolved}
                onShowResolved={setShowResolved}
                me={{ id: user.id, email: user.email, role: user.role }}
                loading={review.loading}
                error={review.error}
                onActivate={openThread}
                onSubmitDraft={async (body, kind) => {
                  if (!reviewDraft) return;
                  const t = await review.create({
                    page: slug,
                    viewport: reviewDraft.viewport,
                    viewportWidth: reviewDraft.viewportWidth >= 200 ? reviewDraft.viewportWidth : undefined,
                    anchor: reviewDraft.anchor,
                    body,
                    kind,
                  });
                  setReviewDraft(null);
                  setActiveThreadId(t.id);
                }}
                onCancelDraft={() => setReviewDraft(null)}
                onReply={(id, body) => review.reply(id, body)}
                onShip={(id, body) => review.reply(id, body, 'shipped')}
                onSetStatus={(id, status) => review.setStatus(id, status)}
                onDelete={async (id) => {
                  await review.remove(id);
                  setActiveThreadId((cur) => (cur === id ? null : cur));
                }}
                onEditSection={editSectionOf}
                onNotifyNow={review.notifyNow}
              />
            )
          ) : (
            renderPanel()
          )}
        </aside>
      </div>
      )}

      {paletteOpen && isDev && (
        <SectionPalette
          globals={gb.blocks}
          onAdd={(t) => {
            const insertAt = selectedSection
              ? (doc?.sections.findIndex((s) => s.key === selectedSection.key) ?? -1) + 1
              : (doc?.sections.length ?? 0);
            const key = ed.addSection(slug, t, insertAt);
            selectSection(key);
            setPaletteOpen(false);
          }}
          onAddGlobal={(blockId) => {
            const insertAt = selectedSection
              ? (doc?.sections.findIndex((s) => s.key === selectedSection.key) ?? -1) + 1
              : (doc?.sections.length ?? 0);
            const key = ed.addSection(slug, 'global', insertAt);
            ed.setSectionData(slug, key, { type: 'global', blockId });
            selectSection(key);
            setPaletteOpen(false);
          }}
          onClose={() => setPaletteOpen(false)}
        />
      )}

      {historyOpen && (
        <HistoryPanel
          slug={slug}
          isDev={isDev}
          onClose={() => setHistoryOpen(false)}
          onRestored={() => {
            setHistoryOpen(false);
            setSel(null);
            // The server replaced the page's blocks — reload so the editor
            // shows the restored doc (drafts for OTHER pages are autosaved
            // well before anyone reaches for History).
            void ed.reload();
            void refreshPending();
          }}
        />
      )}

      {drawerOpen && (
        <NavDrawer
          email={user?.email}
          role={user?.role}
          currentTo={activeList ? `/pages?list=${activeList}` : '/pages'}
          onPick={pickEditorLink}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </div>
  );
}
